import { useEffect, useRef, useState } from 'react';

import type { BlockDiagram } from '@cadstrom/cadstrom-shared';
import type { CompileBlockDiagramWorkerResponse } from './compile-block-diagram.worker.js';

interface CompileRequestInputs {
	document: BlockDiagram.BlockDiagramDocument;
	variant: BlockDiagram.BlockDiagramVariant;
	knobs?: BlockDiagram.BlockDiagramKnobs;
}

/** A compiled diagram together with the variant it was compiled for. */
export interface CompiledBlockDiagramState {
	compiled: BlockDiagram.CompiledBlockDiagram;
	/** Variant of the compiled result; trails the requested variant while a recompile is in flight. */
	variant: BlockDiagram.BlockDiagramVariant;
}

/**
 * Compiles a block-diagram document off the main thread in a dedicated web
 * worker, so large designs never block rendering or input.
 *
 * Requests coalesce latest-wins: while a compile is in flight, newer inputs
 * replace any queued ones, and superseded results are discarded. The previous
 * compiled result is retained during recompiles to avoid flicker.
 *
 * @param document - The stored block-diagram document.
 * @param variant - Which projection to compile.
 * @param knobs - View-time layout/style adjustments.
 * @returns The latest compiled diagram and its variant, or null before the
 *   first compile completes.
 * @throws The compile failure, rethrown during render so an enclosing error
 *   boundary can catch it as if compilation were synchronous.
 */
export function useCompiledBlockDiagram(
	document: BlockDiagram.BlockDiagramDocument,
	variant: BlockDiagram.BlockDiagramVariant,
	knobs?: BlockDiagram.BlockDiagramKnobs,
): CompiledBlockDiagramState | null {
	const [state, setState] = useState<CompiledBlockDiagramState | null>(null);
	const [compileError, setCompileError] = useState<Error | null>(null);
	const workerRef = useRef<Worker | null>(null);
	const requestIdRef = useRef(0);
	const inFlightRef = useRef<CompileRequestInputs | null>(null);
	const queuedRef = useRef<CompileRequestInputs | null>(null);

	/** Marks the request in flight and sends it to the worker. */
	const postCompile = (worker: Worker, inputs: CompileRequestInputs): void => {
		requestIdRef.current += 1;
		inFlightRef.current = inputs;
		worker.postMessage({
			type: 'compile',
			requestId: requestIdRef.current,
			document: inputs.document,
			variant: inputs.variant,
			knobs: inputs.knobs,
		});
	};

	useEffect(() => {
		const worker = new Worker(new URL('./compile-block-diagram.worker.ts', import.meta.url), { type: 'module' });
		workerRef.current = worker;
		worker.onmessage = (event: MessageEvent<CompileBlockDiagramWorkerResponse>) => {
			const response = event.data;
			const inputs = inFlightRef.current;
			inFlightRef.current = null;
			if (response.requestId === requestIdRef.current && inputs !== null) {
				if (response.type === 'result') {
					setState({ compiled: response.compiled, variant: inputs.variant });
				} else {
					setCompileError(new Error('[UseCompiledBlockDiagram_CompileFailed]', { cause: response.error }));
				}
			}
			const queued = queuedRef.current;
			if (queued !== null) {
				queuedRef.current = null;
				postCompile(worker, queued);
			}
		};
		return () => {
			worker.terminate();
			workerRef.current = null;
			inFlightRef.current = null;
			queuedRef.current = null;
		};
	}, []);

	useEffect(() => {
		const worker = workerRef.current;
		if (worker === null) {
			return;
		}
		const inputs: CompileRequestInputs = { document, variant, knobs };
		if (inFlightRef.current !== null) {
			queuedRef.current = inputs;
			return;
		}
		postCompile(worker, inputs);
	}, [document, variant, knobs]);

	if (compileError !== null) {
		throw compileError;
	}
	return state;
}
